<script setup lang="ts">
import { useSettingsStore } from "~/stores/settings";
import { computed, ref, watch, onMounted } from 'vue';


const config = useRuntimeConfig();
const CFIMAGE_URL = config.public.CLOUDFLARE_HOST_URL;

const settingsStore = useSettingsStore();
const settings = settingsStore.settings;

interface GameData {
  id: number;
  name: string;
  provider: {
    name: string;
  };
  image: string;
}

const props = defineProps({
  data: {
    type: Object as () => GameData,
    default: () => ({ id: 0, name: '', provider: { name: '' }, image: '' }),
    required: false,
  },
  shimmer: {
    type: Boolean,
    default: false,
  },
});

const data = ref(props.data);
const imageLoadError = ref(false); 

const validator = () => {
  if (props.shimmer) {
    return true;
  }
  return props.data && props.data.id;
};

watch(
  () => props.data,
  (newVal) => {
    data.value = newVal ?? { id: 0, name: '', provider: { name: '' }, image: '' };
    imageLoadError.value = false; 
  },
  { immediate: true, deep: true }
);

const display = ref('desktop');
onMounted(() => {
  if (typeof isMobileDevice === 'function') {
    display.value = isMobileDevice() ? 'mobile' : 'desktop';
  }
});

function getUrlImageValid(image: string | undefined | null): string {
  if (!image) {
    return '';
  }
  const cleanedCodeImage = image.replace(/-/g, '');
  const isCloudflareCode = cleanedCodeImage.length === 32 && !cleanedCodeImage.includes('.');

  return isCloudflareCode ? `${CFIMAGE_URL}${image}/${display.value}` : image;
}

watch(() => props.shimmer, (isShimmering) => {
    if (!isShimmering && !validator()) {
        console.warn("Data is invalid after shimmer");
    }
});


const imageUrl = computed(() => {
    return getUrlImageValid(props.data?.image);
});


const gameName = computed(() => {
    return props.data?.name || 'Game image';
});

const handleImageError = () => {
  imageLoadError.value = true;
};
</script>

<template>
  <div :title="gameName" class="flex aspect-[10/13] min-w-[calc(50vw-1.8rem)] xs:min-w-[calc(33.33vw-1.6rem)] sm:min-w-[calc(25vw-1.4rem)] md:min-w-[calc(20vw-1.6rem)] lg:min-w-[calc(16.6666666667vw-4.5rem)] xl:min-w-[162px]">
    <div
      v-if="shimmer"
      class="shimmer-effect group relative flex h-full w-full cursor-pointer flex-col rounded-md"
    />

    <div
      v-else-if="data && data.id" v-bind:key="data.id"
      class="group relative flex w-full cursor-pointer flex-col rounded-md overflow-hidden"
    >
      <template v-if="imageUrl && !imageLoadError">
        <img
          :src="imageUrl"
          :alt="gameName"
          loading="lazy"
          class="absolute inset-0 h-full w-full object-cover object-top rounded-md"
          @error="handleImageError" /> </template>
      <template v-else>
        <CardBackground :name="gameName" :size="display" />
      </template>

      <div
        class="bg-full pointer-events-none absolute bottom-0 z-10 flex h-2/3 w-full items-end rounded-b-md"
         >
        <div class="flex w-full select-none flex-col p-2 text-white">
          <p
            class="overflow-hidden overflow-ellipsis whitespace-nowrap text-xs font-bold leading-none tracking-tight sm:text-sm"
          >
            {{ data.name }}
          </p>
          <p class="text-xss sm:text-xs">
            {{ data.provider.name }}
          </p>
        </div>
      </div>
      <NuxtLink
        :to="{ path: `/play/${data.id}` }"
        class="absolute inset-0 z-20 flex h-full w-full cursor-pointer items-center justify-center rounded-md opacity-0 transition-all group-hover:opacity-100"
            :aria-label="`Play ${data.name}`"
      >
        <div class="absolute inset-0 bg-black/60 rounded-md"></div>
        <button
          class="relative z-10 rounded-md bg-brand-primary p-2 text-sm font-bold leading-none text-white hover:bg-brighten-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-opacity-50"
          tabindex="-1" >
          Jogue
        </button>
      </NuxtLink>
      <div
          class="absolute left-0 top-0 z-20 p-2 cursor-pointer text-white"
        >
        <BaseFavoriteToggle :game="data" />
      </div>
    </div>
    <div v-else class="flex h-full w-full items-center justify-center rounded-md bg-gray-200 text-gray-500">
        No game data
    </div>
  </div>
</template>